import revenueDataPool from './revenueDataPool.json'
import { getRevenueDisplayName } from './revenueDisplayName'

type RevenuePeriod = 'month' | 'sum'
type RevenueRecord = {
  paymentDate: string
  [key: string]: unknown
}

type RevenueResponse = {
  code: string
  msg: string
  data: RevenueRecord[]
  count?: number
}

type RevenueDataset = {
  trend: RevenueResponse
  dimension: RevenueResponse
}

type RevenueProductData = Record<RevenuePeriod, RevenueDataset>
type RevenueDataPool = Record<string, RevenueProductData>

type RevenueQuery = {
  dbtIdList?: string[]
  datePeriod?: string
  startDate?: string
  endDate?: string
  pageNum?: number
  pageSize?: number
}

type AdditiveField =
  | 'usdSales'
  | 'usdRevenue'
  | 'cnySales'
  | 'cnyRevenue'
  | 'orderCount'
  | 'buyerCount'
  | 'firstBuyerCount'
  | 'refundAmount'
  | 'refundOrderCount'

type MatchedResponse = {
  dbtId: string
  response: RevenueResponse
}

const dataPool = revenueDataPool as RevenueDataPool
const additiveFields: AdditiveField[] = [
  'usdSales',
  'usdRevenue',
  'cnySales',
  'cnyRevenue',
  'orderCount',
  'buyerCount',
  'firstBuyerCount',
  'refundAmount',
  'refundOrderCount',
]

const getRecordDateRange = (paymentDate: string) => {
  const [startDate, explicitEndDate] = paymentDate.split('至')
  if (explicitEndDate) return { startDate, endDate: explicitEndDate }

  const date = new Date(`${startDate}T00:00:00`)
  const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0)
  return {
    startDate,
    endDate: `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(
      endDate.getDate(),
    ).padStart(2, '0')}`,
  }
}

const isRecordInRange = (paymentDate: string, startDate?: string, endDate?: string) => {
  if (!startDate || !endDate) return true
  const recordRange = getRecordDateRange(paymentDate)
  return recordRange.startDate >= startDate && recordRange.endDate <= endDate
}

const getNumber = (record: RevenueRecord, field: string) => {
  const value = record[field]
  return typeof value === 'number' ? value : 0
}

const round = (value: number, digits = 2) => Number(value.toFixed(digits))
const divide = (numerator: number, denominator: number, digits = 2) =>
  denominator ? round(numerator / denominator, digits) : 0

const createMetricRecord = (paymentDate: string, base: Partial<RevenueRecord> = {}): RevenueRecord => ({
  ...base,
  paymentDate,
  usdSales: 0,
  usdRevenue: 0,
  usdAvgRevenueBuyer: 0,
  usdAvgRevenue: 0,
  cnySales: 0,
  cnyRevenue: 0,
  cnyAvgRevenueBuyer: 0,
  cnyAvgRevenue: 0,
  orderCount: 0,
  buyerCount: 0,
  avgBuyer: 0,
  firstBuyerCount: 0,
  firstBuyerRatio: 0,
  refundAmount: 0,
  refundOrderCount: 0,
})

const finalizeMetricRecord = (record: RevenueRecord) => {
  additiveFields.forEach((field) => {
    record[field] = round(getNumber(record, field))
  })

  const usdSales = getNumber(record, 'usdSales')
  const cnySales = getNumber(record, 'cnySales')
  const buyerCount = getNumber(record, 'buyerCount')
  const orderCount = getNumber(record, 'orderCount')
  const firstBuyerCount = getNumber(record, 'firstBuyerCount')

  return {
    ...record,
    usdAvgRevenueBuyer: divide(usdSales, buyerCount),
    usdAvgRevenue: divide(usdSales, orderCount),
    cnyAvgRevenueBuyer: divide(cnySales, buyerCount),
    cnyAvgRevenue: divide(cnySales, orderCount),
    avgBuyer: divide(orderCount, buyerCount),
    firstBuyerRatio: divide(firstBuyerCount, buyerCount, 6),
  }
}

const aggregateMetricRows = (
  rows: RevenueRecord[],
  paymentDate: string,
  base: Partial<RevenueRecord> = {},
): RevenueRecord => {
  const aggregate = createMetricRecord(paymentDate, base)
  rows.forEach((row) => {
    additiveFields.forEach((field) => {
      aggregate[field] = getNumber(aggregate, field) + getNumber(row, field)
    })
  })
  return finalizeMetricRecord(aggregate)
}

const getGroupingRows = (rows: RevenueRecord[]) =>
  rows.flatMap((row) => (Array.isArray(row.groupingData) ? (row.groupingData as RevenueRecord[]) : []))

const aggregateTrendRows = (rows: RevenueRecord[], paymentDate: string) => {
  const aggregate = aggregateMetricRows(rows, paymentDate, {
    groupName: null,
    dbtId: null,
    appName: null,
    payChnl: null,
    divideChange: null,
    groupingData: [],
  })

  const groupingRows = getGroupingRows(rows)
  const groupingRowsByProduct = new Map<string, RevenueRecord[]>()
  groupingRows.forEach((row) => {
    const key = String(row.dbtId || row.appName || row.groupName || '')
    groupingRowsByProduct.set(key, [...(groupingRowsByProduct.get(key) || []), row])
  })

  aggregate.groupingData = Array.from(groupingRowsByProduct.values()).map((productRows) => {
    const firstRow = productRows[0]
    return aggregateMetricRows(productRows, paymentDate, {
      groupName: firstRow.groupName,
      dbtId: firstRow.dbtId,
      appName: firstRow.appName,
      payChnl: firstRow.payChnl,
      divideChange: null,
      groupingData: null,
    })
  })
  return aggregate
}

const aggregateMonthlyTrendData = (rows: RevenueRecord[]) => {
  const rowsByDate = new Map<string, RevenueRecord[]>()
  rows.forEach((row) => {
    rowsByDate.set(row.paymentDate, [...(rowsByDate.get(row.paymentDate) || []), row])
  })
  return Array.from(rowsByDate.entries())
    .map(([paymentDate, dateRows]) => aggregateTrendRows(dateRows, paymentDate))
    .sort((first, second) => first.paymentDate.localeCompare(second.paymentDate))
}

const getQueryRangeLabel = (params: RevenueQuery, rows: RevenueRecord[]) => {
  if (params.startDate && params.endDate) return `${params.startDate}至${params.endDate}`
  const ranges = rows.map((row) => getRecordDateRange(row.paymentDate))
  const startDate = ranges.map((range) => range.startDate).sort()[0]
  const endDates = ranges.map((range) => range.endDate).sort()
  const endDate = endDates[endDates.length - 1]
  return startDate && endDate ? `${startDate}至${endDate}` : ''
}

const getMatchedResponses = (params: RevenueQuery, type: keyof RevenueDataset): MatchedResponse[] => {
  const period = params.datePeriod as RevenuePeriod
  if (period !== 'month' && period !== 'sum') return []

  const sourcePeriod: RevenuePeriod = period === 'sum' ? 'month' : period
  const dbtIds = [...new Set(params.dbtIdList || [])]
  return dbtIds.flatMap((dbtId) => {
    const response = dataPool[dbtId]?.[sourcePeriod]?.[type]
    if (!response) return []
    return [
      {
        dbtId,
        response: {
          ...response,
          data: response.data.filter((item) => isRecordInRange(item.paymentDate, params.startDate, params.endDate)),
        },
      },
    ]
  })
}

const paginateDimensionData = (data: RevenueRecord[], params: RevenueQuery): RevenueResponse => {
  const pageNum = params.pageNum || 1
  const pageSize = params.pageSize || data.length || 1
  const startIndex = (pageNum - 1) * pageSize
  return {
    code: '0',
    msg: '成功',
    data: data.slice(startIndex, startIndex + pageSize),
    count: data.length,
  }
}

const withDisplayAppName = (dbtId: string, row: RevenueRecord): RevenueRecord => ({
  ...row,
  appName: getRevenueDisplayName(dbtId, String(row.appName || '')),
})

const getDataset = (params: RevenueQuery, type: keyof RevenueDataset): RevenueResponse => {
  const responses = getMatchedResponses(params, type)
  if (!responses.length) {
    return { code: '0', msg: '成功', data: [], ...(type === 'dimension' ? { count: 0 } : {}) }
  }

  const isSummary = params.datePeriod === 'sum'
  if (type === 'trend') {
    const rows = responses.flatMap(({ response }) => response.data)
    if (!rows.length) return { code: '0', msg: '成功', data: [] }
    if (isSummary) {
      return {
        code: '0',
        msg: '成功',
        data: [aggregateTrendRows(rows, getQueryRangeLabel(params, rows))],
      }
    }
    if (responses.length === 1) return responses[0].response
    return { code: '0', msg: '成功', data: aggregateMonthlyTrendData(rows) }
  }

  if (isSummary) {
    const rows = responses.flatMap(({ dbtId, response }) => {
      if (!response.data.length) return []
      const dimensionRows = response.data.map((row) => withDisplayAppName(dbtId, row))
      const firstRow = dimensionRows[0]
      return [
        aggregateMetricRows(dimensionRows, getQueryRangeLabel(params, dimensionRows), {
          appName: firstRow.appName,
          payChnl: firstRow.payChnl,
          country: firstRow.country,
          prodName: firstRow.prodName,
          pkg: firstRow.pkg,
          sku: firstRow.sku,
          appVersion: firstRow.appVersion,
        }),
      ]
    })
    return paginateDimensionData(rows, params)
  }

  return paginateDimensionData(
    responses.flatMap(({ dbtId, response }) => response.data.map((row) => withDisplayAppName(dbtId, row))),
    params,
  )
}

export const selectTrendChart = (params: RevenueQuery) => Promise.resolve(getDataset(params, 'trend'))

export const selectDimensionChart = (params: RevenueQuery) => Promise.resolve(getDataset(params, 'dimension'))

export const withMinimumLoading = async <T>(request: Promise<T>, duration = 2000) => {
  const [response] = await Promise.all([request, new Promise((resolve) => setTimeout(resolve, duration))])
  return response
}
